import posthog from 'posthog-js';

type EventProperties = Record<string, string | number | boolean | null | undefined>;

let organizationAnalyticsProperties: EventProperties = {};
let stopDeadClickTracking: (() => void) | null = null;
const activeTrackedFlows = new Map<string, TrackedFlowState>();

const DEAD_CLICK_CONFIG = {
  clickThreshold: 3,
  clickWindowMs: 2000,
  reactionDelayMs: 800,
  maxElementNameLength: 120,
  maxSignatureDepth: 5,
} as const;

const DEAD_CLICK_TARGET_SELECTOR = [
  '[data-dead-click-target]',
  '[data-ph-capture-attribute-feature_name]',
  '[aria-disabled="true"]',
  '[disabled]',
  'a',
  'button',
  'label',
  'summary',
  'progress',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="progressbar"]',
].join(',');

const DEAD_CLICK_CARD_SELECTOR = [
  '[data-dead-click-card]',
  '[data-dead-click-target="card"]',
  '[data-component="card"]',
  '[class*="card" i]',
].join(',');

const DEAD_CLICK_INACTIVE_SELECTOR = [
  '[aria-disabled="true"]',
  '[disabled]',
  '[class*="disabled" i]',
  '[class*="inactive" i]',
].join(',');

const DEAD_CLICK_PROGRESS_SELECTOR = ['progress', '[role="progressbar"]'].join(',');

const DEAD_CLICK_BUTTON_SELECTOR = [
  'button',
  '[role="button"]',
  '[data-dead-click-target="button"]',
].join(',');

const DEAD_CLICK_EDITABLE_SELECTOR = [
  'input:not([disabled]):not([aria-disabled="true"])',
  'textarea:not([disabled]):not([aria-disabled="true"])',
  'select:not([disabled]):not([aria-disabled="true"])',
  '[contenteditable="true"]',
].join(',');

const DEAD_CLICK_IGNORE_SELECTOR = '[data-dead-click-ignore], [data-analytics-ignore]';

type DeadClickBurst = {
  signature: string;
  clickCount: number;
  lastClickAt: number;
  reported: boolean;
};

type DeadClickTargetCategory =
  | 'inactive_element'
  | 'non_responsive_card'
  | 'misleading_button'
  | 'progress_bar'
  | 'pointer_element';

type TrackedFlowState = {
  openedAt: number;
  interactionStartedAt?: number;
  interactionEventCaptured: boolean;
  properties: EventProperties;
};

type StartTrackedFlowOptions = {
  properties?: EventProperties;
};

type MarkTrackedFlowInteractionOptions = {
  eventName?: string;
  properties?: EventProperties;
};

type CompleteTrackedFlowOptions = {
  completedEventName?: string;
  abandonedEventName?: string;
  abandonmentThresholdSeconds?: number;
  completionReason?: string;
};

type AiPromptSubmittedOptions = {
  aiFeature: string;
  generationType: string;
  prompt?: string | null;
  properties?: EventProperties;
};

const captureFeatureEvent = (eventName: string, properties: EventProperties = {}): void => {
  if (typeof window === 'undefined' || !posthog?.capture) {
    return;
  }

  posthog.capture(eventName, {
    ...organizationAnalyticsProperties,
    ...properties,
  });
};

export const setOrganizationAnalyticsProperties = (properties: EventProperties | null): void => {
  organizationAnalyticsProperties = properties ?? {};
};

const normalizeElementText = (text: string | null | undefined): string => {
  return (text ?? '').replace(/\s+/g, ' ').trim().slice(0, DEAD_CLICK_CONFIG.maxElementNameLength);
};

const getElementAttribute = (element: HTMLElement, attributeName: string): string => {
  return normalizeElementText(element.getAttribute(attributeName));
};

const getAnalyticsAttribute = (element: HTMLElement, attributeName: string): string => {
  return normalizeElementText(
    element.closest<HTMLElement>(`[${attributeName}]`)?.getAttribute(attributeName),
  );
};

const getPointerCursorAncestor = (element: HTMLElement): HTMLElement | null => {
  let currentElement: HTMLElement | null = element;
  let depth = 0;

  while (
    currentElement &&
    currentElement !== document.body &&
    depth < DEAD_CLICK_CONFIG.maxSignatureDepth
  ) {
    if (window.getComputedStyle(currentElement).cursor === 'pointer') {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
    depth += 1;
  }

  return null;
};

const getDeadClickTarget = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  if (target.closest(DEAD_CLICK_IGNORE_SELECTOR) || target.closest(DEAD_CLICK_EDITABLE_SELECTOR)) {
    return null;
  }

  const explicitTarget = target.closest<HTMLElement>(DEAD_CLICK_TARGET_SELECTOR);
  if (explicitTarget) {
    return explicitTarget;
  }

  const cardTarget = target.closest<HTMLElement>(DEAD_CLICK_CARD_SELECTOR);
  if (cardTarget) {
    return cardTarget;
  }

  return getPointerCursorAncestor(target);
};

const getDeadClickTargetCategory = (element: HTMLElement): DeadClickTargetCategory => {
  if (element.matches(DEAD_CLICK_INACTIVE_SELECTOR)) {
    return 'inactive_element';
  }

  if (element.matches(DEAD_CLICK_PROGRESS_SELECTOR)) {
    return 'progress_bar';
  }

  if (element.matches(DEAD_CLICK_CARD_SELECTOR)) {
    return 'non_responsive_card';
  }

  if (element.matches(DEAD_CLICK_BUTTON_SELECTOR)) {
    return 'misleading_button';
  }

  return 'pointer_element';
};

const getElementName = (element: HTMLElement): string => {
  const elementText =
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
      ? getElementAttribute(element, 'placeholder') || getElementAttribute(element, 'name')
      : normalizeElementText(element.textContent);

  return (
    getAnalyticsAttribute(element, 'data-ph-capture-attribute-feature_name') ||
    getElementAttribute(element, 'aria-label') ||
    getElementAttribute(element, 'title') ||
    getElementAttribute(element, 'name') ||
    elementText ||
    element.getAttribute('role') ||
    element.tagName.toLowerCase()
  );
};

const getPageName = (element: HTMLElement): string => {
  return (
    getAnalyticsAttribute(element, 'data-ph-capture-attribute-page') ||
    normalizeElementText(document.title) ||
    window.location.pathname
  );
};

const getElementSignature = (element: HTMLElement): string => {
  const pathParts: string[] = [];
  let currentElement: HTMLElement | null = element;
  let depth = 0;

  while (
    currentElement &&
    currentElement !== document.body &&
    depth < DEAD_CLICK_CONFIG.maxSignatureDepth
  ) {
    const tagName = currentElement.tagName.toLowerCase();
    const id = currentElement.id ? `#${currentElement.id}` : '';
    const role = currentElement.getAttribute('role')
      ? `[role="${currentElement.getAttribute('role')}"]`
      : '';
    const featureName = currentElement.getAttribute('data-ph-capture-attribute-feature_name')
      ? `[feature="${normalizeElementText(
          currentElement.getAttribute('data-ph-capture-attribute-feature_name'),
        )}"]`
      : '';

    pathParts.push(`${tagName}${id}${role}${featureName}`);
    currentElement = currentElement.parentElement;
    depth += 1;
  }

  return [window.location.pathname, getElementName(element), pathParts.join('>')].join('|');
};

const getCurrentPageKey = (): string => {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const getDeadClickProperties = (element: HTMLElement, clickCount: number): EventProperties => {
  const featureName = getAnalyticsAttribute(element, 'data-ph-capture-attribute-feature_name');
  const elementRole = getElementAttribute(element, 'role');

  return {
    page_name: getPageName(element),
    page_path: getCurrentPageKey(),
    element_name: getElementName(element),
    element_type: element.tagName.toLowerCase(),
    element_role: elementRole || undefined,
    feature_name: featureName || undefined,
    target_category: getDeadClickTargetCategory(element),
    click_count: clickCount,
    dead_click_threshold: DEAD_CLICK_CONFIG.clickThreshold,
    dead_click_window_ms: DEAD_CLICK_CONFIG.clickWindowMs,
  };
};

const normalizeAnalyticsFieldName = (fieldName: string): string => {
  return fieldName
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
};

const getSortAppliedEventName = (fieldName: string): string => {
  return `sort_by_${normalizeAnalyticsFieldName(fieldName)}_applied`;
};

const getFilterAppliedEventName = (fieldName: string): string => {
  return `${normalizeAnalyticsFieldName(fieldName)}_filter_applied`;
};
const getElapsedSeconds = (startedAt: number): number => {
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
};

const FeatureTrackingService = {
  captureEvent(eventName: string, properties: EventProperties = {}) {
    captureFeatureEvent(eventName, properties);
  },

  captureSortApplied(fieldName: string, properties: EventProperties = {}) {
    captureFeatureEvent(getSortAppliedEventName(fieldName), properties);
  },

  captureFilterApplied(fieldName: string, properties: EventProperties = {}) {
    captureFeatureEvent(getFilterAppliedEventName(fieldName), properties);
  },

  captureAiPromptSubmitted({
    aiFeature,
    generationType,
    prompt,
    properties = {},
  }: AiPromptSubmittedOptions): void {
    const normalizedPrompt = (prompt ?? '').trim();

    captureFeatureEvent('ai_prompt_submitted', {
      ai_feature: aiFeature,
      generation_type: generationType,
      prompt_length: normalizedPrompt.length,
      ...properties,
    });
  },

  startTrackedFlow(flowKey: string, options: StartTrackedFlowOptions = {}): void {
    if (typeof window === 'undefined' || activeTrackedFlows.has(flowKey)) {
      return;
    }

    activeTrackedFlows.set(flowKey, {
      openedAt: Date.now(),
      interactionEventCaptured: false,
      properties: options.properties ?? {},
    });
  },

  markTrackedFlowInteraction(
    flowKey: string,
    options: MarkTrackedFlowInteractionOptions = {},
  ): void {
    if (typeof window === 'undefined') {
      return;
    }

    let activeFlow = activeTrackedFlows.get(flowKey);
    if (!activeFlow) {
      activeFlow = {
        openedAt: Date.now(),
        interactionEventCaptured: false,
        properties: options.properties ?? {},
      };
      activeTrackedFlows.set(flowKey, activeFlow);
    }

    if (!activeFlow.interactionStartedAt) {
      activeFlow.interactionStartedAt = Date.now();
    }

    activeFlow.properties = {
      ...activeFlow.properties,
      ...options.properties,
    };

    if (options.eventName && !activeFlow.interactionEventCaptured) {
      activeFlow.interactionEventCaptured = true;
      captureFeatureEvent(options.eventName, activeFlow.properties);
    }
  },

  completeTrackedFlow(flowKey: string, options: CompleteTrackedFlowOptions = {}): void {
    if (typeof window === 'undefined') {
      return;
    }

    const activeFlow = activeTrackedFlows.get(flowKey);
    if (!activeFlow) {
      return;
    }

    activeTrackedFlows.delete(flowKey);

    const secondsBeforeExit = getElapsedSeconds(activeFlow.openedAt);
    const abandonmentThresholdSeconds = options.abandonmentThresholdSeconds ?? 0;

    if (options.abandonedEventName && !activeFlow.interactionStartedAt) {
      captureFeatureEvent(options.abandonedEventName, {
        ...activeFlow.properties,
        seconds_before_exit: secondsBeforeExit,
        interaction_started: false,
        abandonment_reason: 'left_before_interaction',
        abandonment_threshold_seconds: abandonmentThresholdSeconds,
      });
      return;
    }

    const secondsAfterInteraction = activeFlow.interactionStartedAt
      ? getElapsedSeconds(activeFlow.interactionStartedAt)
      : undefined;

    if (
      options.abandonedEventName &&
      secondsAfterInteraction !== undefined &&
      secondsAfterInteraction <= abandonmentThresholdSeconds
    ) {
      captureFeatureEvent(options.abandonedEventName, {
        ...activeFlow.properties,
        seconds_before_exit: secondsBeforeExit,
        seconds_after_interaction: secondsAfterInteraction,
        interaction_started: true,
        abandonment_reason: 'early_exit',
        abandonment_threshold_seconds: abandonmentThresholdSeconds,
      });
      return;
    }

    if (options.completedEventName) {
      captureFeatureEvent(options.completedEventName, {
        ...activeFlow.properties,
        seconds_elapsed: secondsAfterInteraction ?? secondsBeforeExit,
        completion_reason: options.completionReason ?? 'exit',
      });
    }
  },

  startDeadClickTracking(): () => void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return () => {};
    }

    if (stopDeadClickTracking) {
      return stopDeadClickTracking;
    }

    let lastReactionAt = 0;
    let activeBurst: DeadClickBurst | null = null;
    const pendingTimers = new Set<number>();

    const markPageReaction = (): void => {
      lastReactionAt = Date.now();
    };

    const reactionEvents: Array<keyof DocumentEventMap> = [
      'change',
      'input',
      'scroll',
      'selectionchange',
      'submit',
    ];

    reactionEvents.forEach((eventName) => {
      document.addEventListener(eventName, markPageReaction, true);
    });
    window.addEventListener('hashchange', markPageReaction);
    window.addEventListener('popstate', markPageReaction);

    const mutationObserver = new MutationObserver(markPageReaction);
    mutationObserver.observe(document.body ?? document.documentElement, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });

    const handlePointerUp = (event: PointerEvent): void => {
      if (event.button !== 0) {
        return;
      }

      const element = getDeadClickTarget(event.target);
      if (!element) {
        return;
      }

      const clickedAt = Date.now();
      const pageKeyAtClick = getCurrentPageKey();
      const timerId = window.setTimeout(() => {
        pendingTimers.delete(timerId);

        if (pageKeyAtClick !== getCurrentPageKey() || lastReactionAt > clickedAt) {
          activeBurst = null;
          return;
        }

        const signature = getElementSignature(element);
        if (
          !activeBurst ||
          activeBurst.signature !== signature ||
          clickedAt - activeBurst.lastClickAt > DEAD_CLICK_CONFIG.clickWindowMs
        ) {
          activeBurst = {
            signature,
            clickCount: 1,
            lastClickAt: clickedAt,
            reported: false,
          };
          return;
        }

        activeBurst.clickCount += 1;
        activeBurst.lastClickAt = clickedAt;

        if (activeBurst.clickCount >= DEAD_CLICK_CONFIG.clickThreshold && !activeBurst.reported) {
          activeBurst.reported = true;
          captureFeatureEvent(
            'dead_click_detected',
            getDeadClickProperties(element, activeBurst.clickCount),
          );
        }
      }, DEAD_CLICK_CONFIG.reactionDelayMs);

      pendingTimers.add(timerId);
    };

    document.addEventListener('pointerup', handlePointerUp, true);

    stopDeadClickTracking = () => {
      document.removeEventListener('pointerup', handlePointerUp, true);
      reactionEvents.forEach((eventName) => {
        document.removeEventListener(eventName, markPageReaction, true);
      });
      window.removeEventListener('hashchange', markPageReaction);
      window.removeEventListener('popstate', markPageReaction);
      mutationObserver.disconnect();
      pendingTimers.forEach((timerId) => window.clearTimeout(timerId));
      pendingTimers.clear();
      activeBurst = null;
      stopDeadClickTracking = null;
    };

    return stopDeadClickTracking;
  },
};

export default FeatureTrackingService;
